// The What's New note: `WHATS-NEW.md` at the repo root, read raw at build time and rendered by `ui/whats-new.tsx`.
//
// ⚠ A TINY HAND-ROLLED PARSER, NOT A MARKDOWN LIBRARY. The file has exactly one shape — a banner, a rule, then lines of
// `- YYYY-MM-DD — sentence` — and a library would render the banner, the HTML comment and anything else somebody pastes.
// Everything above the first `---` is skipped on purpose: the md-banner gate makes every .md open with the guiding
// principle, and the players do not need to read it.

export type WhatsNewSection = { date: string; items: string[] };

const ENTRY = /^\s*[-*]\s+(\d{4}-\d{2}-\d{2})\s*[—–-]\s*(.+?)\s*$/;

/** Entries grouped by date, in file order (the file is written newest first). Lines that aren't entries are ignored. */
export function parseWhatsNew(text: string): WhatsNewSection[] {
  const lines = text.split(/\r?\n/);
  const rule = lines.findIndex((l) => /^---\s*$/.test(l));
  const body = rule >= 0 ? lines.slice(rule + 1) : lines;
  const out: WhatsNewSection[] = [];
  for (const line of body) {
    const m = ENTRY.exec(line);
    if (!m) continue;
    const [, date, sentence] = m as unknown as [string, string, string];
    const last = out[out.length - 1];
    if (last && last.date === date) last.items.push(sentence);
    else out.push({ date, items: [sentence] });
  }
  return out;
}

/** The newest date in the note — what the site "is", for a feedback row's `build`. Null when the note is empty. */
export function latestWhatsNew(text: string): string | null {
  return parseWhatsNew(text)[0]?.date ?? null;
}

/** Today as YYYY-MM-DD in the viewer's own clock — the day the note keys "once a day" on. */
export function localDay(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** "16 September 2026" for a heading. Falls back to the raw string if it isn't a date. */
export function longDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
